import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import { PostHogProvider } from '@posthog/react'
import posthog from 'posthog-js'
import { isPosthogConfigured } from '@/shared/lib/posthog/posthogEnv'
import { sanitizePostHogBeforeSend } from '@/shared/lib/posthog/sanitizePostHogCapture'
import { dropSecretNetworkRequest } from '@/shared/lib/posthog/posthogSecretNetwork'
import { POSTHOG_NO_EXTERNAL_SCRIPTS_OPTIONS } from '@/shared/lib/posthog/posthogNoExternalScripts'

const POSTHOG_DEFAULTS = '2026-01-30' as const

/**
 * Provider em torno da app; sem env não renderiza PostHog (hooks não disponíveis).
 * Init manual após mount evita corrida posthog-js × portal/unmount no StrictMode (SYNAP-DOC-2C).
 */
export function PosthogAppProvider({ children }: { children: ReactNode }) {
  const configured = isPosthogConfigured()
  const apiKey = configured ? import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN!.trim() : ''
  const apiHost = configured ? import.meta.env.VITE_PUBLIC_POSTHOG_HOST!.trim() : ''
  const options = useMemo(
    () =>
      ({
        api_host: apiHost,
        defaults: POSTHOG_DEFAULTS,
        capture_pageview: false,
        capture_pageleave: false,
        before_send: sanitizePostHogBeforeSend,
        ...POSTHOG_NO_EXTERNAL_SCRIPTS_OPTIONS,
        // Gravação segue desligada; máscaras mantidas como defesa em profundidade.
        session_recording: {
          maskAllInputs: true,
          maskCapturedNetworkRequestFn: dropSecretNetworkRequest,
        },
      }) as const,
    [apiHost],
  )

  const initializedRef = useRef(false)

  useEffect(() => {
    if (!configured) return
    if (initializedRef.current) {
      posthog.set_config(options)
      return
    }
    if (!posthog.__loaded) {
      posthog.init(apiKey, options)
    }
    initializedRef.current = true
  }, [configured, apiKey, options])

  if (!configured) {
    return children
  }

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>
}
